import React, { useEffect } from "react";
import { Col, Container, Form, Row, Button } from "react-bootstrap";
import logo from "Assets/images/logo.png";
import { useChatterPeerDetailsQuery, useCreateReferenceLetterMutation, useGetPeerReferenceLetterQuery } from "store/services/chatter/peer";
import LoadingIndicator from "components/shared/LoadingIndicator";
import moment from "moment";
import { isEmpty } from "lodash";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { CalendarIcon, EditIconPink } from "Assets/icons";
import { convertToMomentDate, replacePlaceholders, userFullName } from "helpers/common";
import { Field, useFormik } from "formik";
import LoadingButton from "components/shared/LoadingButton";
import { BlobProvider, pdf, PDFDownloadLink } from "@react-pdf/renderer";
import { ReferenceLetterDoc } from "components/PDF/Chatter/ReferenceLetter";
import { useUploadFileMutation } from "store/services/utility";
import { CHATTER_PEER_DETAILS } from "config/paths";
import KhanReportLogo from "components/shared/KhanReortLogo";
import { REFERENCE_LETTER_EMAIL } from "config/constants";

const CreatePeerReferenceLetter = () => {
  const { id: peerId } = useParams();
  const navigate = useNavigate();

  // get peer details
  const { data: peerDetails, isLoading: loadingPeerDetails } = useChatterPeerDetailsQuery(peerId);

  // get reference letter template
  const { data: referenceLetterData, isLoading } = useGetPeerReferenceLetterQuery();

  // create reference letter
  const [createLetter, { isLoading: createLetterLoading }] = useCreateReferenceLetterMutation();

  // file upload api
  const [uploadFile, { isLoading: isUploading }] = useUploadFileMutation();


  const formik = useFormik({
    initialValues: {
      referenceTextArray: [],
    },
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });

  useEffect(() => {
    if (isEmpty(referenceLetterData) || isEmpty(peerDetails)) {
      return;
    }

    const templateVariables = {
      PEER_FULL_NAME: userFullName(peerDetails),
      START_DATE: convertToMomentDate(peerDetails?.allPrograms?.[0]?.start_date || new Date()),
    };

    const referenceTextArray = referenceLetterData?.template_content.map((template) => {
      let templateString = replacePlaceholders(template, templateVariables);
      return { disabled: true, text: templateString };
    });

    formik.setFieldValue("referenceTextArray", referenceTextArray);
  }, [referenceLetterData, peerDetails]);

  const handleClick = (index) => {
    const updatedArray = [...formik.values.referenceTextArray];
    updatedArray[index].disabled = false;
    formik.setFieldValue("referenceTextArray", updatedArray);
  };

  const handleBlur = (index) => {
    const updatedArray = [...formik.values.referenceTextArray];
    updatedArray[index].disabled = true;
    formik.setFieldValue("referenceTextArray", updatedArray);
  };

  const handleSubmit = async (values) => {
    try {
      const doc =
        <ReferenceLetterDoc
          data={values?.referenceTextArray}
        />

      const asPdf = pdf([]);
      asPdf.updateContainer(doc);
      const blob = await asPdf.toBlob();
      const fileToUpload = new File([blob], "refernce_letter.pdf", { type: blob.type });

      let uploadedFileName = await uploadFile({ file: fileToUpload, acl: "private" }).unwrap();

      if (!uploadedFileName) {
        throw new Error("File not uploaded");
      }

      const payload = {
        peerId,
        data: {
          "attachment_key": uploadedFileName,
          "attachment_name": uploadedFileName,
        }
      }

      await createLetter(payload).unwrap();
      navigate(generatePath(CHATTER_PEER_DETAILS, { id: peerId }));
    } catch (err) {
      console.error("Error saving reference letter:", err);
    }
  }

  return (
    <>
      <section className="referenceLetter position-relative py-5">
        <Container fluid={true}>
          <Row>
            <Col lg="12" className="my-2">
              <div className="cardCstm p-lg-5 p-3 rounded shadow">
                <div className="top text-center">
                  <img src={logo} alt="" className="img-fluid" />
                </div>
                {isLoading || loadingPeerDetails ? (
                  <LoadingIndicator />
                ) : (
                  <Form onSubmit={formik.handleSubmit}>
                    <Row>
                      <Col lg="12" className="my-2">
                        <div className="d-flex align-items-center mb-4">
                          <h6 className="m-0 themeBlue fw-bold">Created Date:</h6>
                          <p className="m-0 text-muted">
                            <span className="icn me-1">
                              <CalendarIcon />
                            </span>
                            {moment().format("DD/MM/YYYY")}
                          </p>
                        </div>
                      </Col>
                      <Col lg="12" className="my-2">
                        <div
                          className="formDetail rounded"
                          style={{ background: "#F9F9F9" }}
                        >
                          <h5 className="m-0 fw-bold themeBlue">
                            To Whom It May Concern,
                          </h5>
                          {formik.values.referenceTextArray?.map((item, index) => (
                            <div className="py-2" key={index}>
                              <div className="iconWithText position-relative">
                                {item.disabled && (
                                  <Button
                                    className="border-0 p-0 position-absolute"
                                    style={{ right: 8, bottom: 8 }}
                                    variant="transparent"
                                    onClick={() => handleClick(index)}
                                  >
                                    <EditIconPink />
                                  </Button>
                                )}
                                <textarea
                                  name={`referenceTextArray.${index}.text`}
                                  onBlur={() => handleBlur(index)}
                                  disabled={item?.disabled}
                                  rows={8}
                                  value={formik.values.referenceTextArray[index]?.text}
                                  onChange={formik.handleChange}
                                  defaultValue={item?.text}
                                  className="form-control h-auto bg-white rounded-3 pb-5 themeBlue"
                                ></textarea>
                              </div>
                            </div>
                          ))}
                        </div>
                      </Col>
                      <Col md="12" className="my-2">
                        <p style={{ fontSize: "16px" }} className="m-0">
                        For more information about this reference please contact our volunteer coordinator at : <a className="themeClr" href={`mailto:${REFERENCE_LETTER_EMAIL}`}>{REFERENCE_LETTER_EMAIL}</a>
                        </p>
                      </Col>
                      <Col md="12" className="my-2 mt-5">
                        <KhanReportLogo />
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="12" className="my-2 mt-5">
                        <LoadingButton
                          type="submit"
                          className="d-flex align-items-center justify-content-center commonBtn"
                          isLoading={formik.isSubmitting}
                        >
                          Save
                        </LoadingButton>
                      </Col>
                    </Row>
                  </Form>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default CreatePeerReferenceLetter;
